import React, { createContext, useContext, useRef, useCallback } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';

const InternalsContext = createContext();

export const useInternals = () => useContext(InternalsContext);

export const InternalsProvider = ({ children }) => {
  const internalsPaginationModeRef = useRef('auto');
  const visitorsPaginationModeRef = useRef('auto');
  const otherPeoplePaginationModeRef = useRef('auto');

  const hasValue = (value) => value !== undefined && value !== null && value !== '';

  const hasLegacyPagination = (params = {}) =>
    hasValue(params?.limit) || hasValue(params?.offset);

  const hasPagePagination = (params = {}) =>
    hasValue(params?.page) || hasValue(params?.page_size);

  const normalizePaginationParams = (params = {}) => {
    const normalized = { ...(params || {}) };

    const hasLimit = normalized.limit !== undefined && normalized.limit !== null && normalized.limit !== '';
    const hasOffset = normalized.offset !== undefined && normalized.offset !== null && normalized.offset !== '';

    if (!hasLimit && !hasOffset) {
      return normalized;
    }

    const limitNumber = Number(normalized.limit);
    const offsetNumber = Number(normalized.offset);
    const hasValidLimit = Number.isFinite(limitNumber) && limitNumber > 0;
    const safeOffset = Number.isFinite(offsetNumber) && offsetNumber >= 0 ? offsetNumber : 0;

    if (normalized.page === undefined && hasValidLimit) {
      normalized.page = Math.floor(safeOffset / limitNumber) + 1;
    }

    if (normalized.page_size === undefined && hasValidLimit) {
      normalized.page_size = limitNumber;
    }

    delete normalized.limit;
    delete normalized.offset;
    return normalized;
  };

  const resolvePaginationAttempt = (params = {}, mode = 'auto') => {
    const cloned = { ...(params || {}) };
    if (mode === 'page') {
      return { using: 'page', params: normalizePaginationParams(cloned) };
    }

    if (mode === 'legacy') {
      return { using: 'legacy', params: cloned };
    }

    if (hasPagePagination(cloned)) {
      return { using: 'page', params: normalizePaginationParams(cloned) };
    }

    return { using: 'legacy', params: cloned };
  };

  const getApiErrorMessage = (error) => {
    const data = error?.response?.data;
    if (!data) return '';

    if (typeof data === 'string' && data.trim()) {
      const raw = data.trim();
      try {
        const parsed = JSON.parse(raw);
        if (typeof parsed?.message === 'string' && parsed.message.trim()) {
          return parsed.message;
        }
        if (typeof parsed?.detail === 'string' && parsed.detail.trim()) {
          return parsed.detail;
        }
      } catch (_) {
        // mantém fallback abaixo com string original
      }
      return raw;
    }

    if (typeof data?.message === 'string' && data.message.trim()) {
      return data.message;
    }
    if (Array.isArray(data?.message) && data.message.length > 0) {
      return String(data.message[0]);
    }
    if (typeof data?.message === 'object' && data.message !== null) {
      for (const value of Object.values(data.message)) {
        if (Array.isArray(value) && value.length > 0) {
          return String(value[0]);
        }
        if (typeof value === 'string' && value.trim()) {
          return value;
        }
      }
    }

    if (typeof data?.detail === 'string' && data.detail.trim()) {
      return data.detail;
    }

    if (Array.isArray(data?.non_field_errors) && data.non_field_errors.length > 0) {
      return String(data.non_field_errors[0]);
    }

    if (typeof data === 'object') {
      for (const value of Object.values(data)) {
        if (Array.isArray(value) && value.length > 0) {
          return String(value[0]);
        }
        if (typeof value === 'string' && value.trim()) {
          return value;
        }
      }
    }

    return '';
  };

  // Requisicao listar home
  const listInternalHome = useCallback(async (params) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      const response = await api.get('internals/internal/async/home/', {
        params,
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar dados da home:', error);
      toast.error('Erro ao buscar dados da home');
    }
  }, []);

  // Quantitativo detalhes dos cardes
  const listQuantitative = async (params) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      const query = new URLSearchParams(params).toString();
      const response = await api.get(`internals/internal/async/cards_detail?${query}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return response.data;
    } catch (error) {
      console.error("Erro ao listar quantitativos", error);
      toast.error("Erro ao listar quantitativos");
    }
  };

  //Requisição Listar internos
  const listInternals = async (params) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    const originalParams = { ...(params || {}) };
    const hasPaginationParams = hasLegacyPagination(originalParams) || hasPagePagination(originalParams);
    const firstAttempt = resolvePaginationAttempt(originalParams, internalsPaginationModeRef.current);

    try {
      const response = await api.get('internals/internal/async/', {
        params: firstAttempt.params,
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      // console.log('Resposta da api TODOS INTERNOS:', response.data)
      // console.log('URL enviada:', response.request.responseURL);
      // console.log('Parâmetros enviados:', params);
      if (hasPaginationParams) {
        internalsPaginationModeRef.current = firstAttempt.using;
      }
      return response.data;
    } catch (error) {
      const shouldTryFallback = error?.response?.status === 400 && hasPaginationParams;
      if (shouldTryFallback) {
        const fallbackUsing = firstAttempt.using === 'legacy' ? 'page' : 'legacy';
        const fallbackAttempt = resolvePaginationAttempt(originalParams, fallbackUsing);
        const isDifferentAttempt =
          JSON.stringify(fallbackAttempt.params) !== JSON.stringify(firstAttempt.params);

        if (!isDifferentAttempt) {
          console.error('Erro ao buscar internos:', error);
          toast.error('Erro ao buscar internos');
          return;
        }

        try {
          const fallbackResponse = await api.get('internals/internal/', {
            params: fallbackAttempt.params,
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          internalsPaginationModeRef.current = fallbackUsing;
          return fallbackResponse.data;
        } catch (fallbackError) {
          console.error('Erro ao buscar internos:', fallbackError);
          toast.error('Erro ao buscar internos');
          return;
        }
      }

      console.error('Erro ao buscar internos:', error);
      toast.error('Erro ao buscar internos');
    }
  };

  // Requisição para obter uma internos específica pelo ID
  const getInternalById = async (internalId) => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        return;
      }

      const response = await api.get(`internals/internal/${internalId}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      return response.data;
    } catch (error) {
      console.error('Erro ao buscar internos pelo ID', error);
      toast.error('Erro ao buscar internos pelo ID');
    }
  };

  // Requisição para listar internos de uma cela específica
  const listInternalsByCell = async (cellId) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.get(`internals/internal/cell/${cellId}/`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 404 && error.response.data?.message) {
        toast.info(error.response.data.message);
      } else {
        toast.error("Erro ao buscar internos da cela.");
      }
    }
  };

  // Função para listar internos por nome sem token
  const listInternalsName = async (name = '') => {
    const sanitizedName = String(name).trim();
    if (!sanitizedName) {
      return [];
    }

    try {
      const encodedName = encodeURIComponent(sanitizedName);
      const response = await api.get(`internals/internal/searchname/${encodedName}`, {
        skipAuth: true,
        skip401Redirect: true,
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar internos', error);
      if (error?.response?.status === 401 || error?.response?.status === 404) {
        return [];
      }
      toast.error('Erro ao buscar internos');
      return [];
    }
  };

  // Requisição cadastra interno
  const createInternal = async (formData) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.post('internals/internal/', formData, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      toast.success('Interno cadastrada com sucesso!');
      return response.data;
    } catch (error) {
      console.error('Erro ao cadastrar interno', error);
      const message = getApiErrorMessage(error);
      toast.error(message || 'Erro ao cadastrar internos');
      return null;
    }
  };

  // Ficha de inclusão do interno
  const listInternalInclusion = async (params) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.get('internals/internalinclusion/', {
        params,
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar ficha de inclusão', error);
      toast.error('Erro ao buscar ficha de inclusão');
    }
  };

  const createInternalInclusion = async (formData) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.post('internals/internalinclusion/', formData, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      toast.success('Ficha de inclusão salva com sucesso!');
      return response.data;
    } catch (error) {
      console.error('Erro ao salvar ficha de inclusão', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Erro ao salvar ficha de inclusão');
      }
      return null;
    }
  };

  const updateInternalInclusion = async (id, formData) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.patch(`internals/internalinclusion/${id}/`, formData, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      toast.success('Ficha de inclusão atualizada com sucesso!');
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar ficha de inclusão', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Erro ao atualizar ficha de inclusão');
      }
      return null;
    }
  };

  // Requisição Editar interno
  const updateInternal = async (internalId, formatDate) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.patch(`internals/internal/${internalId}/`, formatDate, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      toast.success('Dados do interno atualizados com sucesso')
      return response.data;
    } catch (error) {
      console.error('Erro ao salvar interno', error);
      if (error.response && error.response.data && error.response.data.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Erro ao cadastrar internos');
      }
    }
  };

  // Da entrada no interno existente
  const entryInternal = async (formData) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const isMultipart = typeof FormData !== 'undefined' && formData instanceof FormData;
      const response = await api.post('internals/internal/newentry/', formData, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          ...(isMultipart ? { 'Content-Type': 'multipart/form-data' } : {}),
        }
      });

      toast.success('Entrada cadastrada com sucesso!');
      return response.data;
    } catch (error) {
      console.error('Erro ao cadastrar Entrada', error);
      const message = getApiErrorMessage(error);
      toast.error(message || 'Erro ao cadastrar Entradas');
      return null;
    }
  };

  // Busca específica do interno
  const searchInternal = async (data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      // Mapeia os campos do data para os parâmetros corretos
      const params = new URLSearchParams();
      if (data.cpf) params.append('c', data.cpf);
      if (data.name) params.append('n', data.name);
      if (data.date_of_birth) params.append('a', data.date_of_birth);
      if (data.mothers_name) params.append('m', data.mothers_name);

      const response = await api.get(`internals/internal/search/?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar internos', error);
    }
  };

  // Listar Regime do interno
  const listInternalRegime = async (internalId) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }
    try {
      const response = await api.get('/internals/internalregime', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Erro ao listar regime:", error);
      throw error;
    }
  };
  // Listar conduta do interno
  const listInternalConduct = async (internalId) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }
    try {
      const response = await api.get('/internals/internalconduct', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Erro ao listar conduta:", error);
      throw error;
    }
  };

  // Requisição Listar Facção
  const listFactions = async () => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.get('internals/faction/', {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      return response.data;
    } catch (error) {
      console.error('Erro ao listar facção', error);
      toast.error('Erro ao listar facção');
    }
  };

  // Requisição Listar função com faccao
  const listFunctionFaction = async () => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.get('internals/function_faction/', {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      return response.data;
    } catch (error) {
      console.error('Erro ao listar função com facção', error);
      toast.error('Erro ao listar função com facção');
    }
  };

  // Requisição Listar Relação com faccao
  const listFactionrelationship = async (id) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.get(`internals/factionrelationship/${id}/`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao listar relação com facção', error);
      toast.error('Erro ao listar relação com facção');
    }
  };

  // Requisição cadastra factionrelationship
  const createFactionrelationship = async (formData) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.post('internals/factionrelationship/', formData, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      toast.success('Dados de facção cadastrada com sucesso!');
      return response.data;
    } catch (error) {
      console.error('Erro ao cadastrar facção', error)
      if (error.response && error.response.data && error.response.data.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Erro ao cadastrar facção');
      }
    }
  };

  // Requisição Editar factionrelationship
  const updateFactionrelationship = async (internalId, formatDate) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.patch(`internals/factionrelationship/${internalId}/`, formatDate, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      toast.success('Dados de facção atualizados com sucesso')
      return response.data;
    } catch (error) {
      console.error('Erro ao salvar dados', error);
      if (error.response && error.response.data && error.response.data.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Erro ao atualizar facção');
      }
    }
  };

  // Requisição Salvar imagem do interno
  const uploadInternalImage = async (formData) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      console.error('Token de acesso não encontrado');
      return;
    }

    try {
      const response = await api.post('/internals/internalimage/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${accessToken}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao salvar imagem', error);
      throw error;
    }
  };

  // Requisição Listar imagems do interno
  const listInternalImage = async (internalId) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.get(`/internals/internalimage/internal/${internalId}/`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao listar imagens', error);
      toast.error('Erro ao listar imagens');
    }
  };

  // Requisição marcar imagem principal
  const updateInternalImageMain = async (imageId) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.patch(`/internals/internalimage/${imageId}/`, { main: true }, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar imagem principal', error);
      toast.error('Erro ao atualizar imagem principal');
    }
  };

  // Requisição Listar Type de imagem
  const listTypeImage = async () => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.get('internals/typeimage/', {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      return response.data;
    } catch (error) {
      console.error('Erro ao listar Tipode de imagem', error);
      toast.error('Erro ao listar Tipode de imagem');
    }
  };

  // Requisição Listar detailsType de imagem
  const listDetailType = async () => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.get('internals/detail_type_image/', {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao listar  Tipo de de imagem', error);
      toast.error('Erro ao listar Tipo de de imagem');
    }
  };

  // Requisição listar Enderecos por interno
  const listAddressByInternal = async (internalId) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.get(`internals/internaladdress/internal/${internalId}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      return response.data.results;
    } catch (error) {
      console.error('Erro ao listar endereços', error);
      toast.error('Erro ao listar endereços');
    }
  };

  // Requisição listar Enderecos todos interno
  const listAddress = async (params) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      // Log dos parâmetros enviados
      console.log('Parâmetros enviados para a API de endereços:', params);

      const response = await api.get('internals/internaladdress/', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params, // Enviando os parâmetros aqui
      });

      // Log da resposta completa da API

      return response.data.results;
    } catch (error) {
      console.error('Erro ao listar endereços', error);
      toast.error('Erro ao listar endereços');
    }
  };

  // Requisição adcionar Enderecos do interno
  const createAddress = async (addressData) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.post('internals/internaladdress/', addressData, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      toast.success('Endereço adicionado com sucesso')
      return response.data;
    } catch (error) {
      console.error('Erro ao adicionar endereço', error);
      toast.error('Erro ao adicionar endereço');
    }
  };

  // Requisição editar Enderecos do interno
  const updateAddress = async (addressData) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.patch(`internals/internaladdress/${addressData.id}/`, addressData, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      toast.success('Endereço atualizado com sucesso')
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar endereço', error);
      toast.error('Erro ao atualizar endereço');
    }
  };

  // Requisição listar documentos do interno
  const listFiles = async (internalId) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.get(`internals/internalfile/internal/${internalId}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao listar documentos', error);
      const apiMessage = getApiErrorMessage(error);
      toast.error(apiMessage || 'Erro ao listar documentos');

    }
  };

  // Requisição editar Enderecos do interno
  const addFiles = async (formData) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.post('internals/internalfile/', formData, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Documentos adicionado com sucesso')
      return response.data;
    } catch (error) {
      console.error('Erro ao Salvar documentos', error);
      const apiMessage = getApiErrorMessage(error);
      toast.error(apiMessage || 'Erro ao Salvar documentos');
      return null;
    }
  };

  // Requisição adicionar telefone do interno
  const createPhone = async (phoneData) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.post('internals/internalphone/', phoneData, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      toast.success('Telefone adicionado com sucesso')
      return response.data;
    } catch (error) {
      console.error('Erro ao adicionar Telefone', error);
      toast.error('Erro ao adicionar Telefone');
    }
  };

  // Requisição editar telefone do interno
  const updatePhone = async (phoneData) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.patch(`internals/internalphone/${phoneData.id}/`, phoneData, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      toast.success('Telefone atualizado com sucesso')
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar Telefone', error);
      toast.error('Erro ao atualizar Telefone');
    }
  };

  // Requisição listar telefone do interno
  const listPhone = async (internalId) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.get('internals/internalphone/', {
        headers: {
          Authorization: `Bearer ${accessToken}`
        },
        params: { internal: internalId }
      });

      return response.data.results;
    } catch (error) {
      console.error('Erro ao listar telefone', error);
      toast.error('Erro ao listar telefone');
    }
  };

  // Requisição adicionar processos
  const addProcess = async (formData) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.post('internals/internalprocess/', formData, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Processo adicionado com sucesso');
      return response.data;
    } catch (error) {
      console.error('Erro ao adicionar processo', error);
      const apiMessage = getApiErrorMessage(error);
      toast.error(apiMessage || 'Erro ao adicionar processo');
      return null;
    }
  };

  // Lista de comarcas (judicial districts) para Processo
  const listProcessJudicialDistricts = async () => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return [];
    }

    try {
      const response = await api.get('internals/internalprocess/list/judicial_districts', {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      const payload = response?.data || {};
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload.judicial_districts)) return payload.judicial_districts;
      if (Array.isArray(payload.results)) return payload.results;

      const firstArray = Object.values(payload).find((value) => Array.isArray(value));
      return Array.isArray(firstArray) ? firstArray : [];
    } catch (error) {
      console.error('Erro ao listar comarcas', error);
      toast.error('Erro ao listar comarcas');
      return [];
    }
  };

  // Lista de capitulações penais para Processo (filtro por artigo/tipificação)
  const listProcessCriminalClassification = async (searchTerm = '') => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return [];
    }

    try {
      const response = await api.get('internals/internalprocess/list/criminal_classification', {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      const payload = response?.data || {};
      let items = [];

      if (Array.isArray(payload)) {
        items = payload;
      } else if (Array.isArray(payload.criminal_classification)) {
        items = payload.criminal_classification;
      } else if (Array.isArray(payload.results)) {
        items = payload.results;
      } else {
        const firstArray = Object.values(payload).find((value) => Array.isArray(value));
        items = Array.isArray(firstArray) ? firstArray : [];
      }

      const query = String(searchTerm || '').trim().toLowerCase();

      if (!query) return items;

      return items.filter((item) => {
        const article = String(item?.art || item?.article || '').toLowerCase();
        const typification = String(item?.tipificacao || item?.typification || item?.common_name || '').toLowerCase();
        const chapter = String(item?.capitulo || item?.chapter || '').toLowerCase();
        const title = String(item?.titulo || item?.title || '').toLowerCase();
        return (
          article.includes(query)
          || typification.includes(query)
          || chapter.includes(query)
          || title.includes(query)
        );
      });
    } catch (error) {
      console.error('Erro ao listar capitulação penal', error);
      toast.error('Erro ao listar capitulação penal');
      return [];
    }
  };

  // Requisição para obter processo do interno por id
  const getProcessInternalById = async (internalId) => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        return;
      }
      const response = await api.get(`internals/internalprocess/internal/${internalId}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar processo', error);
      const apiMessage = getApiErrorMessage(error);
      toast.error(apiMessage || 'Erro ao buscar processo');
    }
  }

  // Requisição para obter processo id
  const getProcessById = async (processId) => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        return;
      }

      const response = await api.get(`internals/internalprocess/${processId}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      return response.data;
    } catch (error) {
      console.error('Erro ao buscar processo', error);
      const apiMessage = getApiErrorMessage(error);
      toast.error(apiMessage || 'Erro ao buscar processo');
    }
  };

  // Requisição para obter processos passando parms
  const getProcessInternals = async (params) => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        return;
      }

      const response = await api.get(`internals/internalprocess/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params,
      });

      return response.data;
    } catch (error) {
      console.error('Erro ao buscar internos', error);
      const apiMessage = getApiErrorMessage(error);
      toast.error(apiMessage || 'Erro ao buscar internos');
    }
  };

  // Requisição editar processo
  const updateProcess = async (id, data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
      const response = await api.patch(`internals/internalprocess/${id}/`, data, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          ...(isFormData ? { 'Content-Type': 'multipart/form-data' } : {})
        }
      });
      toast.success('Processo atualizado com sucesso')
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar processo', error);
      const apiMessage = getApiErrorMessage(error);
      toast.error(apiMessage || 'Erro ao atualizar processo');
      return null;
    }
  };

  // Requisição para obter ciclos
  const getCicles = async (params) => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        return;
      }
      const response = await api.get('internals/cycle/', {
        headers: {
          Authorization: `Bearer ${accessToken}`
        },
        params
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar ciclos', error);
      toast.error('Erro ao buscar ciclos');
    }
  };

  // Requisição para atualizar ciclo
const updateCicle = async (cycleId, data) => {
  const accessToken = localStorage.getItem('accessToken');
  if (!accessToken) {
    return null;
  }

  try {
    const response = await api.patch(`internals/cycle/${cycleId}/`, data, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    toast.success('Data de entrada atualizada com sucesso!');
    return response.data;
  } catch (error) {
    console.error('Erro ao atualizar ciclo', error);
    const apiMessage = getApiErrorMessage(error);
    toast.error(apiMessage || 'Erro ao atualizar data de entrada');
    return null;
  }
};

  // Requisição para atualizar transferência
const updateTransfer = async (transferId, data) => {
  const accessToken = localStorage.getItem('accessToken');
  if (!accessToken) {
    return null;
  }

  try {
    const response = await api.patch(`internals/transfer/${transferId}/`, data, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    toast.success('Data de entrada atualizada com sucesso!');
    return response.data;
  } catch (error) {
    console.error('Erro ao atualizar transferência', error);
    const apiMessage = getApiErrorMessage(error);
    toast.error(apiMessage || 'Erro ao atualizar data de entrada');
    return null;
  }
};

  // Transferencia de Cela
  const changeCell = async (cellChangeData) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.patch('internals/internal/cell/changes/', cellChangeData, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      toast.success('Cela alterada com sucesso')
      return response.data;
    } catch (error) {
      toast.error(error.response.data.message);
    }
  };

  // Transferência de interno de unidade
  const createTransfer = async (transferData) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.post('internals/transfer/', transferData, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      toast.success('Transferência criada com sucesso!');
      return response.data;
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message || 'Erro ao criar transferências');
      } else {
        toast.error('Erro ao criar transferências');
      }
    }
  };

  // Listar todas as transferências
  const listTransfers = async (params = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      console.error("Erro: Token de acesso não encontrado");
      return { results: [] };
    }

    try {
      const response = await api.get('internals/transfer', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params,
      });

      return response.data;
    } catch (error) {
      if (error.response?.status === 403) {
        return { results: [] }; // silencia 403
      }

      console.error("Erro ao buscar transferências:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Erro ao buscar transferências');
      }

      return { results: [] };
    }
  };

  // Aceitar transferência
  const acceptTransfer = async (internalId, cell, dateRecipient) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.patch(`internals/transfer/received/${internalId}/`, { cell, date_recipient: dateRecipient }, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      toast.success('Transferência recebida com sucesso!');
      return response.data;
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message || 'Erro ao aceitar transferência');
      } else {
        toast.error('Erro ao aceitar transferência');
      }
    }
  };

  // Recusar transferência
  const rejectTransfer = async (internalId, reason) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
    const response = await api.patch(`internals/transfer/nonreceipt/${internalId}/`, { no_receipt: reason }, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      toast.success('Transferência recusada com sucesso!');
      return response.data;
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message || 'Erro ao recusar transferência');
      } else {
        toast.error('Erro ao recusar transferência');
      }
    }
  };

  // Cancelar transferencia
  const cancelTransfer = async (transferId) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.patch(`internals/transfer/cancel/${transferId}/`, {}, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      toast.success('Transferência cancelada com sucesso');
      return response.data;
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Erro ao cancelar transferência');
      }
    }
  };

  // Listar tipos de saída
  const listTypeLeaving = async () => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return [];

    try {
      const response = await api.get('/internals/typeleaving/', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao listar tipos de saída', error);
      toast.error('Erro ao listar tipos de saída');
      return [];
    }
  };

  const createLeavingRecord = async (data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    const response = await api.post('/internals/internal/leavingrecord/', data, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  };

  // Listar tipos de entrada (fluxo unificado de entrada/retorno)
  const listTypeEntry = async (params = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return [];

    try {
      const response = await api.get('/internals/typeentry/', {
        headers: { Authorization: `Bearer ${accessToken}` },
        params,
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao listar tipos de entrada', error);
      toast.error('Erro ao listar tipos de entrada');
      return [];
    }
  };

  const createEntryRecord = async (data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    const isMultipart = typeof FormData !== 'undefined' && data instanceof FormData;
    const response = await api.post('/internals/entryrecord/', data, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        ...(isMultipart ? { 'Content-Type': 'multipart/form-data' } : {}),
      },
    });
    return response.data;
  };

  const listTemporaryLeavingRecords = async (params = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return { count: 0, next: null, previous: null, results: [] };
    }

    try {
      const response = await api.get('/internals/leavingrecord/', {
        headers: {
          Authorization: `Bearer ${accessToken}`
        },
        params,
      });
      return response?.data || { count: 0, next: null, previous: null, results: [] };
    } catch (error) {
      console.error('Erro ao buscar registros de saída temporária', error);
      return { count: 0, next: null, previous: null, results: [] };
    }
  };

  const getLastPendingTemporaryLeavingRecord = async (internalId) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken || !internalId) {
      return null;
    }

    try {
      const response = await api.get('/internals/leavingrecord/lastpending/', {
        headers: {
          Authorization: `Bearer ${accessToken}`
        },
        params: {
          internal: internalId,
        },
      });
      return response?.data || null;
    } catch (error) {
      if (error?.response?.status === 404) {
        return null;
      }
      console.error('Erro ao buscar último registro pendente de saída temporária', error);
      return null;
    }
  };

  const internalTemporaryReturn = async (data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    const response = await api.patch('/internals/internal/temporaryreturn/', data, {
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });
    return response.data;
  };

  const internalRecapture = async (data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;
    const response = await api.patch('/internals/internal/recapture/', data, {
      headers: { Authorization: `Bearer ${accessToken}` }
    });
    return response.data;
  };

  const internalHospitalReturn = async (data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;
    const response = await api.patch('/internals/internal/hospitalreturn/', data, {
      headers: { Authorization: `Bearer ${accessToken}` }
    });
    return response.data;
  };

  // Requisições serviço social
  const addSocialService = async (data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.post('internals/receptionsocialservice/', data, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Dados serviço social adicionados com sucesso');
      return response.data;
    } catch (error) {
      console.error('Erro ao adicionar dados serviço social', error);
      toast.error('Erro ao adicionar dados serviço social');
    }
  };

  const updateSocialService = async (id, data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.patch(`internals/receptionsocialservice/${id}/`, data, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Dados de serviço social atualizados com sucesso');
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar dados de serviço sosial', error);
      toast.error('Erro ao atualizar dados de serviço sosial');
    }
  };

  const listSocialService = async (internalId) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.get(`internals/receptionsocialservice/internal/${internalId}/`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar dados de serviço social do interno', error);
      toast.error('Erro ao buscar dados de serviço social do interno');
    }
  };

  // Assitencia de serviso social(evolução)
  const addAssistance = async (data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.post('internals/assistance/', data, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Dados adicionados com sucesso');
      return response.data;
    } catch (error) {
      console.error('Erro ao adicionar dados', error);
      toast.error('Erro ao adicionar dados');
    }
  };

  const listAssistance = async (internalId) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }
    try {
      const response = await api.get(`/internals/assistance/internal/${internalId}/`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      return response.data.results;
    } catch (error) {
      console.error('Erro ao buscar dados de evolução', error);
      toast.error('Erro ao buscar dados de evolução');
    }
  };

  // Requisições de psicologia
  const addPsychology = async (data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.post('/internals/receptionpsychology/', data, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      toast.success('Psicologia registrada com sucesso');
      return response;
    } catch (error) {
      console.error('Erro ao registrar psicologia', error);
      toast.error('Erro ao registrar psicologia');
    }
  };

  const listPsychology = async (internalId) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }
    try {
      const response = await api.get(`/internals/receptionpsychology/internal/${internalId}/`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar dados de psicologia', error);
      toast.error('Erro ao buscar dados de psicologia');
    }
  };

  const updatePsychology = async (id, data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.patch(`/internals/receptionpsychology/${id}/`, data, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        }
      });

      toast.success('Dados atualizados com sucesso');
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar dados', error);
      toast.error('Erro ao atualizar dados');
    }
  };

  const addPsychologicalCare = async (data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.post('/internals/psychologicalcare/', data, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        }
      });

      toast.success('Dados adicionados com sucesso');
      return response.data;
    } catch (error) {
      console.error('Erro ao adicionar dados', error);
      toast.error('Erro ao adicionar dados');
    }
  };

  const listPsychologicalCare = async (internalId) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }
    try {
      const response = await api.get(`/internals/psychologicalcare/internal/${internalId}/`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar dados de psicologia', error);
      toast.error('Erro ao buscar dados de psicologia');
    }
  };

  // Requisições de educacao
  const addEducation = async (data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.post('/internals/receptioneducational/', data, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      toast.success('Educação registrada com sucesso');
      return response;
    } catch (error) {
      console.error('Erro ao registrar Educação', error);
      toast.error('Erro ao registrar Educação');
    }
  };

  const listEducation = async (internalId) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }
    try {
      const response = await api.get(`/internals/receptioneducational/internal/${internalId}/`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar dados de educação', error);
      toast.error('Erro ao buscar dados de educação');
    }
  };

  const updateEducation = async (id, data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.patch(`/internals/receptioneducational/${id}/`, data, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        }
      });

      toast.success('Dados atualizados com sucesso');
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar dados', error);
      toast.error('Erro ao atualizar dados');
    }
  };

  const addEducationEvolution = async (data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.post('/internals/educationalevolution/', data, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        }
      });

      toast.success('Dados adicionados com sucesso');
      return response.data;
    } catch (error) {
      console.error('Erro ao adicionar dados', error);
      toast.error('Erro ao adicionar dados');
    }
  };

  const listEducationEvolution = async (internalId) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }
    try {
      const response = await api.get(`/internals/educationalevolution/internal/${internalId}/`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar dados de educação', error);
      toast.error('Erro ao buscar dados de educação');
    }
  };

  // requisições de nutrição
  const createNutrition = async (data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.post('internals/nutritionalstatus/', data, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      toast.success('atendimento registrada com sucesso');
      return response;
    } catch (error) {
      console.error('Erro ao registrar nutrição', error);
      toast.error('Erro ao registrar nutrição');
    }
  };

  const listNutrition = async (params) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      const response = await api.get('/internals/nutritionalstatus/', {
        headers: { Authorization: `Bearer ${accessToken}` },
        params,
      });

      return response.data;
    } catch (error) {
      console.error('Erro ao buscar dados de nutrição', error);
      toast.error('Erro ao buscar dados de nutrição');
      return [];
    }
  };

  const updateNutrition = async (nutritionalId, nutritionalData) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.patch(`internals/nutritionalstatus/${nutritionalId}/`, nutritionalData, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        }
      });

      toast.success('Dados atualizados com sucesso');
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar dados de nutrição', error);
      toast.error('Erro ao atualizar dados de nutrição');
    }
  };

  const addNutritionEvolution = async (data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.post('/internals/nutritionalevolution/', data, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      toast.success('Evolução registrada com sucesso');
      return response.data;
    } catch (error) {
      console.error('Erro ao adicionar evolução', error);
      toast.error('Erro ao adicionar evolução');
      throw error; // Lança o erro para depuração adicional
    }
  };

  const listNutritionEvolution = async (params) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      const response = await api.get('/internals/nutritionalevolution/', {
        headers: { Authorization: `Bearer ${accessToken}` },
        params,
      });

      return response.data;
    } catch (error) {
      console.error('Erro ao buscar dados de evolução', error);
      toast.error('Erro ao buscar dados de evolução');
      return [];
    }
  };


  // Cria um novo registro de vacinação
  const createVaccination = async (data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;
    try {
      const response = await api.post('internals/internalvaccination/', data, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      toast.success('Vacina registrado com sucesso');

      return response.data;
    } catch (error) {
      console.error('Erro ao registrar vacinação', error);
      toast.error('Erro ao registrar vacinação');
    }
  };


  // Lista os registros de vacinação de um interno
  const listVaccinationsByInternal = async (internalId) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      const response = await api.get(`internals/internalvaccination/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: {
          internal: internalId, // ✅ Correção: Passa apenas o ID
          start_date: '2025-01-01', // ✅ Garantindo que a API retorne os dados completos
        },
      });

      return response.data;
    } catch (error) {
      console.error('❌ Erro ao buscar vacinação:', error);
      toast.error('Erro ao buscar vacinação.');
      return [];
    }
  };

  // Cria um novo registro de dosagem de remédio
  const createDosage = async (data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;
    try {
      const response = await api.post('internals/internaldosage/', data, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      toast.success('Medicação registrada com sucesso');
      return response.data;
    } catch (error) {
      console.error('Erro ao registrar dosagem', error);
      toast.error('Erro ao registrar dosagem');
    }
  };

  const updateDosage = async (id, data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;
    try {
      const response = await api.patch(`internals/internaldosage/${id}/`, data, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      toast.success('Medicação atualizada com sucesso');
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar dosagem', error);
      toast.error('Erro ao atualizar dosagem');
    }
  };

  // Lista os registros de remédios de um interno
  const listDosageInternal = async (internalId) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;
    try {
      const response = await api.get('internals/internaldosage/', {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: {
          internal: internalId,
          start_date: '2025-01-01' // ✅ Garante que os dados corretos são retornados
        },
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar dosagem', error);
      toast.error('Erro ao buscar dosagem');
      return [];
    }
  };


  // Cria um novo registro de atendimento externo
  const createExternalHealth = async (data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;
    try {
      const response = await api.post('internals/externalhealth/', data, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      toast.success('Atendimento externo registrado com sucesso');
      return response.data;
    } catch (error) {
      console.error('Erro ao registrar atendimento externo', error);
      toast.error('Erro ao registrar atendimento externo');
    }
  };

  const listExternalHealthByInternal = async (internalId) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      const response = await api.get('internals/externalhealth/', {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: {
          internal: internalId,
          start_date: '2025-01-01'
        },
      });

      return response.data;
    } catch (error) {
      console.error('❌ Erro ao buscar atendimento externo:', error);
      toast.error('Erro ao buscar atendimento externo.');
      return [];
    }
  };

  // Requisicoes de atentimento interno
  const createInternalHealthCare = async (data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      const response = await api.post('internals/internalhealth/', data, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      toast.success('consulta registrado com sucesso!');
      return response.data;
    } catch (error) {
      console.error('Erro ao registrar atendimento interno:', error);
      toast.error('Erro ao registrar atendimento interno.');
    }
  };

  // internalContext.js
  const listInternalHealthCare = async (internalId) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      const response = await api.get('internals/internalhealth/', {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: {
          internal: internalId,
          start_date: '2025-01-01'
        },
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar atendimentos internos:', error);
      toast.error('Erro ao buscar atendimentos internos.');
      return [];
    }
  };

  const updateInternalHealthCare = async (id, data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      const response = await api.patch(`internals/internalhealth/${id}/`, data, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      toast.success('Atendimento interno atualizado com sucesso!');
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar atendimento interno:', error);
      toast.error('Erro ao atualizar atendimento interno.');
    }
  };

  // requisições de saude geral
  const listHealthInternal = async (params) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;
    try {
      const response = await api.get('internals/generalhealth/', {
        headers: { Authorization: `Bearer ${accessToken}` },
        params,
      });

      return response.data;
    } catch (error) {
      console.error('Erro ao buscar dados de saúde', error);
      toast.error('Erro ao buscar dados de saúde');
      return [];
    }
  };

  // Create Health
  const createHealth = async (data, hasFile = false) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      const response = await api.post('internals/generalhealth/', data, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          ...(hasFile ? { 'Content-Type': 'multipart/form-data' } : {}),
        },
      });
      toast.success('Saúde registrada com sucesso');
      return response.data;
    } catch (error) {
      console.error('Erro ao registrar saúde', error);
      toast.error('Erro ao registrar saúde');
    }
  };

  // Update Health
  const updateHealth = async (healthId, data, hasFile = false) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      const response = await api.patch(`internals/generalhealth/${healthId}/`, data, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          ...(hasFile ? { 'Content-Type': 'multipart/form-data' } : {}),
        },
      });
      toast.success('Dados atualizados com sucesso');
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar dados de saúde', error);
      toast.error('Erro ao atualizar dados de saúde');
    }
  };

  const listAdmissions = async (params) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      const response = await api.get('internals/admission/', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params,
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar admissões', error);
      toast.error('Erro ao buscar admissões');
      return [];
    }
  };

  const createAdmission = async (data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      const response = await api.post('internals/admission/', data, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      toast.success('Admissão registrada com sucesso');
      return response.data;
    } catch (error) {
      console.error('Erro ao registrar admissão', error);
      toast.error('Erro ao registrar admissão');
    }
  };

  const updateAdmission = async (id, data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      const response = await api.patch(`internals/admission/${id}/`, data, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      toast.success('Admissão atualizada com sucesso');
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar admissão', error);
      toast.error('Erro ao atualizar admissão');
    }
  };

  const listSpontaneousDemand = async (params) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      const response = await api.get('internals/spontaneousdemand/', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params,
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar demanda espontânea', error);
      toast.error('Erro ao buscar demanda espontânea');
      return [];
    }
  };

  const createSpontaneousDemand = async (data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      const response = await api.post('internals/spontaneousdemand/', data, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      toast.success('Demanda espontânea registrada');
      return response.data;
    } catch (error) {
      console.error('Erro ao registrar demanda espontânea', error);
      toast.error('Erro ao registrar demanda espontânea');
    }
  };

  const listTuberculosis = async (params) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      const response = await api.get('internals/tuberculosis/', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params,
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar tuberculose', error);
      toast.error('Erro ao buscar tuberculose');
      return [];
    }
  };

  const createTuberculosis = async (data, hasFile = false) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      const response = await api.post('internals/tuberculosis/', data, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          ...(hasFile ? { 'Content-Type': 'multipart/form-data' } : {}),
        },
      });
      toast.success('Questionário de tuberculose salvo');
      return response.data;
    } catch (error) {
      console.error('Erro ao registrar tuberculose', error);
      toast.error('Erro ao registrar tuberculose');
    }
  };

  const updateTuberculosis = async (id, data, hasFile = false) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      const response = await api.patch(`internals/tuberculosis/${id}/`, data, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          ...(hasFile ? { 'Content-Type': 'multipart/form-data' } : {}),
        },
      });
      toast.success('Questionário de tuberculose atualizado');
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar tuberculose', error);
      toast.error('Erro ao atualizar tuberculose');
    }
  };

  const listTuberculosisQuestions = async (params) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      const response = await api.get('auxiliaries/questions', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params,
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar questões de tuberculose', error);
      return [];
    }
  };

  const listTuberculosisCatalogs = async () => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      const response = await api.get('internals/tuberculosis/catalogs/', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar catálogos de tuberculose', error);
      return {};
    }
  };

  const listHanseniase = async (params) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      const response = await api.get('internals/hanseniase/', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params,
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar hanseníase', error);
      toast.error('Erro ao buscar hanseníase');
      return [];
    }
  };

  const createHanseniase = async (data, hasFile = false) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      const response = await api.post('internals/hanseniase/', data, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          ...(hasFile ? { 'Content-Type': 'multipart/form-data' } : {}),
        },
      });
      toast.success('Questionário de hanseníase salvo');
      return response.data;
    } catch (error) {
      console.error('Erro ao registrar hanseníase', error);
      toast.error('Erro ao registrar hanseníase');
    }
  };

  const updateHanseniase = async (id, data, hasFile = false) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      const response = await api.patch(`internals/hanseniase/${id}/`, data, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          ...(hasFile ? { 'Content-Type': 'multipart/form-data' } : {}),
        },
      });
      toast.success('Questionário de hanseníase atualizado');
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar hanseníase', error);
      toast.error('Erro ao atualizar hanseníase');
    }
  };

  const addHealthEvolution = async (data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.post('/internals/generalhealthevolution/', data, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      toast.success('Evolução registrada com sucesso');
      return response.data;
    } catch (error) {
      console.error('Erro ao adicionar evolução', error);
      toast.error('Erro ao adicionar evolução');
      throw error; // Lança o erro para depuração adicional
    }
  };

  const listHealthEvolution = async (internalId) => {
    if (!internalId) {
      return [];
    }

    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      const response = await api.get('/internals/generalhealthevolution/', {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: { internal: internalId },
      });

      return response.data;
    } catch (error) {
      console.error('Erro ao buscar dados de evolução', error);
      toast.error('Erro ao buscar dados de evolução');
      return [];
    }
  };

  const updateHealthEvolution = async (id, data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;
    try {
      const response = await api.patch(`/internals/generalhealthevolution/${id}/`, data, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      toast.success('Evolução atualizada com sucesso');
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar evolução', error);
      toast.error('Erro ao atualizar evolução');
      throw error;
    }
  };

  const listMedicalTest = async (internalId, params = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return [];
    try {
      const response = await api.get('/internals/internalmedicaltest/', {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: {
          internal: internalId,
          start_date: '2025-01-01',
          ...params,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar exames médicos:', error);
      toast.error('Erro ao buscar exames médicos');
      return [];
    }
  };
  const createMedicalTest = async (data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.post('/internals/internalmedicaltest/', data, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      toast.success('Teste registrada com sucesso');
      return response.data;
    } catch (error) {
      console.error('Erro ao adicionar teste', error);
      toast.error('Erro ao adicionar teste');
      throw error;
    }
  };

  const listExaminations = async (params = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      const response = await api.get('internals/internalexamination/', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params,
      });
      return response.data; // ✅ Certifique-se de retornar os dados
    } catch (error) {
      console.error('Erro ao listar exames:', error);
      toast.error('Erro ao listar exames');
      return { results: [] }; // ✅ Adicione retorno seguro
    }
  };

  const createExamination = async (formData) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      await api.post('/internals/internalexamination/', formData, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      toast.success('Exame cadastrado com sucesso!');
    } catch (error) {
      console.error('Erro ao cadastrar exame:', error);
      toast.error('Erro ao cadastrar exame');
    }
  };

  const updateExamination = async (id, data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      const response = await api.patch(`/internals/internalexamination/${id}/`, data, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      toast.success('Dados atualizados com sucesso!');
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar dados', error);
      toast.error(error.response?.data?.message || 'Erro ao atualizar dados');
    }
  };



  // Requisições de sinais vitais
  const listVitalSigns = async (params) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      const response = await api.get('/internals/internalvitalsigns/', {
        headers: { Authorization: `Bearer ${accessToken}` },
        params,
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar sinais vitais', error);
      toast.error(error.response?.data?.message || 'Erro ao buscar sinais vitais');
    }
  };

  const createVitalSigns = async (data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      const response = await api.post('/internals/internalvitalsigns/', data, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      toast.success('Sinais vitais registrados com sucesso!');
      return response.data;
    } catch (error) {
      console.error('Erro ao registrar sinais vitais', error);
      toast.error(error.response?.data?.message || 'Erro ao registrar sinais vitais');
    }
  };

  const updateVitalSigns = async (id, data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      const response = await api.patch(`/internals/internalvitalsigns/${id}/`, data, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      toast.success('Sinais vitais atualizados com sucesso!');
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar sinais vitais', error);
      toast.error(error.response?.data?.message || 'Erro ao atualizar sinais vitais');
    }
  };

  // Criar Encaminhamento (tabela nova)
  const createForwarding = async (data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      const response = await api.post('/internals/internalforwarding/', data, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      toast.success('Encaminhamento criado com sucesso!');
      return response.data;
    } catch (error) {
      console.error('Erro ao criar encaminhamento:', error);
      toast.error(error.response?.data?.message || 'Erro ao criar encaminhamento.');
    }
  };

  // Listar Encaminhamentos por interno
  const listForwardings = async (internalId) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return [];

    try {
      const response = await api.get('/internals/internalforwarding/', {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: { internal: internalId }
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao listar encaminhamentos:', error);
      toast.error(error.response?.data?.message || 'Erro ao listar encaminhamentos.');
      return [];
    }
  };

  // Atualizar Encaminhamento
  const updateForwarding = async (id, data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      const response = await api.patch(`/internals/internalforwarding/${id}/`, data, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      toast.success('Encaminhamento atualizado com sucesso!');
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar encaminhamento:', error);
      toast.error(error.response?.data?.message || 'Erro ao atualizar encaminhamento.');
    }
  };

  // Criar Prescrição (tabela nova)
  const createPrescription = async (data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      const response = await api.post('/internals/medical_prescription/', data, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      toast.success('Prescrição criada com sucesso!');
      return response.data;
    } catch (error) {
      console.error('Erro ao criar prescrição:', error);
      toast.error(error.response?.data?.message || 'Erro ao criar prescrição.');
    }
  };

  // Listar Prescrições por interno
  const listPrescriptions = async (internalId) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return [];

    try {
      const response = await api.get('/internals/medical_prescription/', {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: { internal: internalId }
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao listar prescrições:', error);
      toast.error(error.response?.data?.message || 'Erro ao listar prescrições.');
      return [];
    }
  };

  // Atualizar Prescrição
  const updatePrescription = async (id, data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      const response = await api.patch(`/internals/medical_prescription/${id}/`, data, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      toast.success('Prescrição atualizada com sucesso!');
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar prescrição:', error);
      toast.error(error.response?.data?.message || 'Erro ao atualizar prescrição.');
    }
  };


  // requisições de odonto
  const createOdontology = async (odontologyData) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.post('/internals/odontology/', odontologyData, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      toast.success('Dados registrada com sucesso');
      return response;
    } catch (error) {
      console.error('Erro ao registrar dados', error);
      toast.error('Erro ao registrar dados');
    }
  };

  const listOdontology = async (internalId) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }
    try {
      const response = await api.get(`/internals/odontology/internal/${internalId}/`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar dados de odonto', error);
      toast.error('Erro ao buscar dados de odonto');
    }
  };

  const updateOdontology = async (odontologyId, odontologyData) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.patch(`/internals/odontology/${odontologyId}/`, odontologyData, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        }
      });

      toast.success('Dados atualizados com sucesso');
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar dados de odonto', error);
      toast.error('Erro ao atualizar dados de odonto');
    }
  };

  const addOdontologyEvolution = async (data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.post('/internals/odontologyevolution/', data, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        }
      });

      toast.success('Dados adicionados com sucesso');
      return response.data;
    } catch (error) {
      console.error('Erro ao adicionar dados', error);
      toast.error('Erro ao adicionar dados');
    }
  };

  const listOdontologyEvolution = async (internalId) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }
    try {
      const response = await api.get(`/internals/odontologyevolution/internal/${internalId}/`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar dados de evolução', error);
      toast.error('Erro ao buscar dados de evolução');
    }
  };

  // Requisicoes de agendamento 
  const createSchedule = async (data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.post('/internals/scheduling/', data, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      toast.success('Agendamento criado com sucesso');
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 400) {
        toast.warning(error.response.data.message || 'Erro ao agendar');
        throw new Error('Erro ao agendar'); // Lança o erro para ser tratado no componente
      } else {
        console.log(error)
        toast.error('Erro ao agendar');
        throw new Error('Erro ao agendar'); // Lança o erro genérico
      }
    }
  };

  const listSchedule = async (params = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return [];
    }

    try {
      const response = await api.get(`/internals/scheduling/`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params,
      });
      return response.data || [];
    } catch (error) {
      console.error('Erro ao buscar agendamentos', error);
      toast.error('Erro ao buscar agendamentos.');
      return [];
    }
  };

  const updateSchedule = async (scheduleId, data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      console.log("Payload enviado:", JSON.stringify(data, null, 2));
      const response = await api.patch(`/internals/scheduling/${scheduleId}/`, data, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        }
      });

      return response.data;
    } catch (error) {
      console.error('Erro em agendamento', error);
      toast.error('Erro ao realizar ação');
    }
  };

  // Requisiçao visitantes
  const createVisitor = async (data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      toast.error('Sessão expirada. Faça login novamente.');
      return;
    }

    try {
      const response = await api.post('internals/visitor/familyvisitor/', data, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Dados enviados! Acompanhe andamento no e-mail cadastrado.');
      return response.data;
    } catch (error) {
      console.error('Erro ao enviar dados', error);
      toast.error(error.response?.data?.message || 'Erro! verifique os dados');
    }
  };

  // Criar outros visitantes
  const createVisitorOthers = async (data, endpoint = '/internals/visitor/others/') => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }
    try {
      const response = await api.post(endpoint, data, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        skip401Redirect: true,
      });
      toast.success('Visitante cadastrado com sucesso!');
      return response.data;
    } catch (error) {
      console.error('Erro ao cadastrar visitante:', error);
      const status = error?.response?.status;
      let data = error?.response?.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch {
          // mantém string
        }
      }
      const baseMessage =
        (typeof data === 'string' && data) ||
        data?.message ||
        data?.detail ||
        data?.error?.message ||
        data?.error ||
        (Array.isArray(data?.errors) && data.errors[0]) ||
        (Array.isArray(data?.non_field_errors) && data.non_field_errors[0]);

      // Alguns endpoints (ex: cadastro de advogado) retornam um detalhe extra em oab_response.
      const oabDetail = typeof data?.oab_response?.message === 'string' ? data.oab_response.message : '';
      const apiMessage = oabDetail && baseMessage && oabDetail !== baseMessage
        ? `${baseMessage} ${oabDetail}`
        : (oabDetail || baseMessage);

      if (status === 401 && !apiMessage) {
        toast.error('Sessão expirada ou sem permissão para cadastrar visitante.');
      } else {
        toast.error(apiMessage || 'Erro ao cadastrar visitante. Verifique os dados.');
      }
      throw error;
    }
  };

  // Consulta a regularidade da inscrição de um advogado na OAB. Não bloqueia o cadastro,
  // serve apenas para preencher automaticamente nome/email e exibir a situação atual.
  const consultLawyerOab = async (registration, uf) => {
    if (!registration || !uf) return null;
    const accessToken = localStorage.getItem('accessToken');

    try {
      const response = await api.get('/internals/visitor/oab/integration/lawyer/consult/', {
        params: { registration, uf },
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
        skip401Redirect: true,
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao consultar situação da OAB:', error);
      return null;
    }
  };

  const listOtherPeople = async (params = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return { results: [] };
    }

    const originalParams = { ...(params || {}) };
    const hasPaginationParams = hasLegacyPagination(originalParams) || hasPagePagination(originalParams);
    const firstAttempt = resolvePaginationAttempt(originalParams, otherPeoplePaginationModeRef.current);

    try {
      const response = await api.get('/internals/other_people/', {
        params: firstAttempt.params,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (hasPaginationParams) {
        otherPeoplePaginationModeRef.current = firstAttempt.using;
      }
      return response.data || { results: [] };
    } catch (error) {
      const shouldTryFallback = error?.response?.status === 400 && hasPaginationParams;
      if (shouldTryFallback) {
        const fallbackUsing = firstAttempt.using === 'legacy' ? 'page' : 'legacy';
        const fallbackAttempt = resolvePaginationAttempt(originalParams, fallbackUsing);
        const isDifferentAttempt =
          JSON.stringify(fallbackAttempt.params) !== JSON.stringify(firstAttempt.params);

        if (!isDifferentAttempt) {
          console.error('Erro ao listar padrinhos:', error);
          return { results: [] };
        }

        try {
          const fallbackResponse = await api.get('/internals/other_people/', {
            params: fallbackAttempt.params,
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          });
          otherPeoplePaginationModeRef.current = fallbackUsing;
          return fallbackResponse.data || { results: [] };
        } catch (fallbackError) {
          console.error('Erro ao listar padrinhos:', fallbackError);
          return { results: [] };
        }
      }

      console.error('Erro ao listar padrinhos:', error);
      return { results: [] };
    }
  };

  const createOtherPeople = async (data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.post('/internals/other_people/', data, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        skip401Redirect: true,
      });
      toast.success('Padrinho cadastrado com sucesso!');
      return response.data;
    } catch (error) {
      console.error('Erro ao cadastrar padrinho:', error);
      let data = error?.response?.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch {
          // Mantem string original
        }
      }

      const apiMessage =
        (typeof data === 'string' && data) ||
        data?.message ||
        data?.detail ||
        data?.error?.message ||
        data?.error ||
        (Array.isArray(data?.errors) && data.errors[0]) ||
        (Array.isArray(data?.non_field_errors) && data.non_field_errors[0]);

      toast.error(apiMessage || 'Erro ao cadastrar padrinho.');
      throw error;
    }
  };

  // Get pra listar visitantes
  const getVisitors = async (params) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    const originalParams = { ...(params || {}) };
    const hasPaginationParams = hasLegacyPagination(originalParams) || hasPagePagination(originalParams);
    const firstAttempt = resolvePaginationAttempt(originalParams, visitorsPaginationModeRef.current);

    try {
      const response = await api.get('/internals/visitor/', {
        params: firstAttempt.params,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (hasPaginationParams) {
        visitorsPaginationModeRef.current = firstAttempt.using;
      }
      return response.data;
    } catch (error) {
      const shouldTryFallback = error?.response?.status === 400 && hasPaginationParams;
      if (shouldTryFallback) {
        const fallbackUsing = firstAttempt.using === 'legacy' ? 'page' : 'legacy';
        const fallbackAttempt = resolvePaginationAttempt(originalParams, fallbackUsing);
        const isDifferentAttempt =
          JSON.stringify(fallbackAttempt.params) !== JSON.stringify(firstAttempt.params);

        if (!isDifferentAttempt) {
          console.error('Erro ao buscar visitantes:', error);
          toast.error('Erro ao buscar visitantes.');
          return { results: [] };
        }

        try {
          const fallbackResponse = await api.get('/internals/visitor/', {
            params: fallbackAttempt.params,
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          });
          visitorsPaginationModeRef.current = fallbackUsing;
          return fallbackResponse.data;
        } catch (fallbackError) {
          console.error('Erro ao buscar visitantes:', fallbackError);
          toast.error('Erro ao buscar visitantes.');
          return { results: [] };
        }
      }

      console.error('Erro ao buscar visitantes:', error);
      toast.error('Erro ao buscar visitantes.');
      return { results: [] };
    }
  };

  const getVisitorById = async (visitorId) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken || !visitorId) {
      return;
    }

    try {
      const response = await api.get(`/internals/visitor/${visitorId}/`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar visitante:', error);
      toast.error('Erro ao buscar dados do visitante.');
    }
  };

  const getVisitorsByInternalId = async (internalId) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken || !internalId) {
      return [];
    }

    const normalizeVisitorsPayload = (payload) => {
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload?.results)) return payload.results;
      return [];
    };

    const isVisitorLinkedToInternal = (visitor) => {
      const internals = Array.isArray(visitor?.internals) ? visitor.internals : [];
      return internals.some((internal) => String(internal?.id || internal) === String(internalId));
    };

    const uniqueVisitorsById = (visitors = []) => Array.from(
      new Map(
        visitors
          .filter(Boolean)
          .map((visitor, index) => [
            String(visitor?.id || `${visitor?.cpf || ''}-${visitor?.name || ''}-${index}`),
            visitor,
          ])
      ).values()
    );

    const fetchWithInternalQueryParam = async () => {
      const pageSize = 100;
      const maxPages = 20;
      let offset = 0;
      let currentPage = 0;
      const linkedVisitors = [];

      while (currentPage < maxPages) {
        const response = await getVisitors({ internal: internalId, limit: pageSize, offset });
        const visitorsPage = normalizeVisitorsPayload(response).filter(isVisitorLinkedToInternal);

        if (!visitorsPage.length && currentPage === 0) {
          return [];
        }

        if (!visitorsPage.length) {
          break;
        }

        linkedVisitors.push(...visitorsPage);

        const hasNext =
          response &&
          typeof response === 'object' &&
          Object.prototype.hasOwnProperty.call(response, 'results') &&
          Boolean(response?.next);

        if (!hasNext) {
          break;
        }

        offset += pageSize;
        currentPage += 1;
      }

      return uniqueVisitorsById(linkedVisitors);
    };

    try {
      // Backend corrigido: filtro por interno no endpoint principal.
      return await fetchWithInternalQueryParam();
    } catch (error) {
      console.error('Erro ao buscar visitantes por query param internal. Usando fallback.', error);

      try {
        // Fallback legado (rota dedicada antiga)
        const response = await api.get(`/internals/visitor/internals/${internalId}/`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        const scopedVisitors = normalizeVisitorsPayload(response?.data).filter(isVisitorLinkedToInternal);
        return uniqueVisitorsById(scopedVisitors);
      } catch (legacyError) {
        console.error('Erro no fallback legado ao buscar visitantes do interno:', legacyError);

        try {
          const pageSize = 100;
          const maxPages = 20;
          let offset = 0;
          let currentPage = 0;
          const linkedVisitors = [];

          while (currentPage < maxPages) {
            const response = await getVisitors({ limit: pageSize, offset });
            const visitorsPage = normalizeVisitorsPayload(response);

            if (!visitorsPage.length) {
              break;
            }

            linkedVisitors.push(...visitorsPage.filter(isVisitorLinkedToInternal));

            const hasNext =
              response &&
              typeof response === 'object' &&
              Object.prototype.hasOwnProperty.call(response, 'results') &&
              Boolean(response?.next);

            if (!hasNext) {
              break;
            }

            offset += pageSize;
            currentPage += 1;
          }

          return uniqueVisitorsById(linkedVisitors);
        } catch (fallbackError) {
          console.error('Erro no fallback final ao buscar visitantes do interno:', fallbackError);
          if (fallbackError?.response?.status !== 404) {
            toast.error('Erro ao buscar visitantes do interno.');
          }
          return [];
        }
      }
    }
  };

  const listVisitorFiles = async (visitorId) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return { results: [] };
    }

    try {
      const response = await api.get('/internals/visitorfile/', {
        params: visitorId ? { visitor: visitorId } : {},
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      return response.data || { results: [] };
    } catch (error) {
      console.error('Erro ao buscar arquivos do visitante:', error);
      return { results: [] };
    }
  };

  const listVisitorAddresses = async (visitorId) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return [];
    try {
      const response = await api.get(`/internals/visitoraddress/visitor/${visitorId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return response.data || [];
    } catch (error) {
      console.error('Erro ao buscar endereços do visitante:', error);
      return [];
    }
  };

  const createVisitorAddress = async (data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;
    const response = await api.post('/internals/visitoraddress/', data, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  };

  const updateVisitorAddress = async (addressId, data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;
    const response = await api.patch(`/internals/visitoraddress/${addressId}`, data, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  };

  const listVisitorPhone = async (visitorId) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return [];
    try {
      const response = await api.get(`/internals/visitorphone/visitor/${visitorId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const data = response.data;
      return Array.isArray(data) ? data : data?.results || (data ? [data] : []);
    } catch (error) {
      console.error('Erro ao buscar telefones do visitante:', error);
      return [];
    }
  };

  const createVisitorPhone = async (phoneData) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;
    try {
      const response = await api.post('/internals/visitorphone/', phoneData, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      toast.success('Telefone adicionado com sucesso');
      return response.data;
    } catch (error) {
      console.error('Erro ao adicionar telefone do visitante:', error);
      toast.error('Erro ao adicionar telefone');
    }
  };

  const updateVisitorPhone = async (phoneId, phoneData) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;
    try {
      const response = await api.patch(`/internals/visitorphone/${phoneId}/`, phoneData, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      toast.success('Telefone atualizado com sucesso');
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar telefone do visitante:', error);
      toast.error('Erro ao atualizar telefone');
    }
  };

  const listVisitorRelationships = async (visitorId) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return [];
    try {
      const response = await api.get('/internals/visitorrelationship/', {
        params: { visitor: visitorId },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return response.data?.results || response.data || [];
    } catch (error) {
      console.error('Erro ao buscar relacionamentos do visitante:', error);
      return [];
    }
  };

  const createVisitorRelationship = async (data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;
    const response = await api.post('/internals/visitorrelationship/', data, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  };

  const updateVisitorRelationship = async (id, data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;
    const response = await api.patch(`/internals/visitorrelationship/${id}/`, data, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  };

  const deleteVisitorRelationship = async (id) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;
    await api.delete(`/internals/visitorrelationship/${id}/`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  };

  const listVisitorImages = async (visitorId) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return { results: [] };
    }

    const requestConfigs = [
      {
        url: '/internals/visitorimage/',
        params: visitorId ? { visitor: visitorId } : {},
      },
      {
        url: '/internals/internalimage/',
        params: visitorId ? { visitor: visitorId } : {},
      },
      {
        url: '/internals/internalimage/',
        params: visitorId ? { person: visitorId } : {},
      },
    ];

    for (const config of requestConfigs) {
      try {
        const response = await api.get(config.url, {
          params: config.params,
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        return response.data || { results: [] };
      } catch (error) {
        const status = error?.response?.status;
        if (status === 404 || status === 400) {
          continue;
        }
        console.error('Erro ao buscar imagens do visitante:', error);
        return { results: [] };
      }
    }

    return { results: [] };
  };

  const createVisitorFile = async (formData) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.post('/internals/visitorfile/', formData, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success('Documento adicionado com sucesso!');
      return response.data;
    } catch (error) {
      console.error('Erro ao adicionar documento do visitante:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Erro ao adicionar documento do visitante.');
      }
      throw error;
    }
  };

  const updateVisitor = async (visitorId, payload) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken || !visitorId) {
      return;
    }

    try {
      const response = await api.patch(`/internals/visitor/${visitorId}/`, payload, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      toast.success('Visitante atualizado com sucesso!');
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar visitante:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Erro ao atualizar visitante.');
      }
      throw error;
    }
  };

  const respondVisitorAuthorization = async (visitorId, payload) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken || !visitorId) {
      return;
    }

    try {
      const response = await api.patch(`/internals/visitor/response/${visitorId}/`, payload, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      toast.success('Autorização atualizada com sucesso!');
      return response.data;
    } catch (error) {
      console.error('Erro ao responder autorização do visitante:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Erro ao atualizar autorização do visitante.');
      }
      throw error;
    }
  };

  // Requisicao de movimentacao
  const createMoviment = async (data) => {
    try {
      const accessToken = localStorage.getItem('accessToken'); // Captura o token do localStorage
      const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
      const response = await api.post('/internals/unitmoviment/', data, {
        headers: {
          ...(isFormData ? { 'Content-Type': 'multipart/form-data' } : { 'Content-Type': 'application/json' }),
          Authorization: `Bearer ${accessToken}`,
        },
        skip401Redirect: true,
      });
      toast.success('Movimentação registrada com sucesso!');
      return response.data;
    } catch (error) {
      if (error.response) {
        console.error('erro ao criar movimentação:', error)
        let data = error.response.data;
        if (typeof data === 'string') {
          try {
            data = JSON.parse(data);
          } catch {
            // mantém string
          }
        }
        const apiMessage =
          (typeof data === 'string' && data) ||
          data?.message ||
          data?.detail ||
          data?.error?.message ||
          data?.error ||
          (Array.isArray(data?.errors) && data.errors[0]) ||
          (Array.isArray(data?.non_field_errors) && data.non_field_errors[0]);

        if (error.response.status === 401 && !apiMessage) {
          toast.error('Sessão expirada ou sem permissão para registrar movimentação.');
        } else {
          toast.error(apiMessage || 'Erro ao criar movimentação');
        }
        return { error: true, message: apiMessage || 'Erro ao criar movimentação' };
      } else {
        toast.error('Erro ao criar movimentação. verifique os dados');
        return { error: true, message: 'Erro ao criar movimentação. verifique os dados' };
      }
    }
  };

  const updateMovimentRegister = async (movimentId, registerFile) => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken || !movimentId || !registerFile) {
        return null;
      }

      const formData = new FormData();
      formData.append('register', registerFile);

      const response = await api.patch(`/internals/unitmoviment/${movimentId}/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${accessToken}`,
        },
        skip401Redirect: true,
      });

      return response.data;
    } catch (error) {
      if (error.response) {
        let data = error.response.data;
        if (typeof data === 'string') {
          try {
            data = JSON.parse(data);
          } catch {
            // mantem string
          }
        }

        const apiMessage =
          (typeof data === 'string' && data) ||
          data?.message ||
          data?.detail ||
          data?.error?.message ||
          data?.error ||
          (Array.isArray(data?.errors) && data.errors[0]) ||
          (Array.isArray(data?.non_field_errors) && data.non_field_errors[0]);

        toast.error(apiMessage || 'Movimentação criada, mas não foi possível enviar o registro.');
      } else {
        toast.error('Movimentação criada, mas não foi possível enviar o registro.');
      }
      return null;
    }
  };

  // Função para buscar todas as movimentações (GET)
  const getMoviments = async (params = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }
    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await api.get('/internals/unitmoviment/', {
        params,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar movimentações:', error);
      toast.error('Erro ao buscar movimentações.');
      return [];
    }
  };

  // Requisição para criar entrega de sacolão para interno
  const createPackageDelivery = async (data) => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await api.post('/internals/internalpackagedelivery/', data, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        skip401Redirect: true,
      });
      toast.success('Sacolão registrado com sucesso!');
      return response.data;
    } catch (error) {
      if (error.response) {
        console.error('Erro ao registrar sacolão:', error);
        let data = error.response.data;
        if (typeof data === 'string') {
          try {
            data = JSON.parse(data);
          } catch {
            // mantém string
          }
        }

        const apiMessage =
          (typeof data === 'string' && data) ||
          data?.message ||
          data?.detail ||
          data?.error?.message ||
          data?.error ||
          (Array.isArray(data?.errors) && data.errors[0]) ||
          (Array.isArray(data?.non_field_errors) && data.non_field_errors[0]);

        if (error.response.status === 401) {
          toast.error(apiMessage || 'Visitante ou interno sem permissão para entrega do sacolão.');
        } else {
          toast.error(apiMessage || 'Erro ao registrar sacolão.');
        }
      } else {
        toast.error('Erro ao registrar sacolão. Verifique os dados.');
      }
      return null;
    }
  };

  // Requisição para listar entregas de sacolão (paginação e filtros no servidor)
  const listPackageDeliveries = async (params = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      const response = await api.get('/internals/internalpackagedelivery/', {
        params,
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar entregas de sacolão:', error);
      toast.error('Erro ao buscar entregas de sacolão.');
      return { results: [], count: 0 };
    }
  };

  // Requisição para criar um novo registro de trabalho
  const createWork = async (data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }
    try {
      const response = await api.post('/internals/work/', data, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });
      return response;
    } catch (error) {
      console.error("Erro ao criar trabalho:", error);
      throw error;
    }
  };

  // Requisição para listar trabalhos por nome do interno
  // A API só permite filtrar /internals/work/ por internal_name (não existe filtro por id).
  // Como internos podem ter nomes duplicados, buscamos todas as páginas pelo nome e
  // filtramos no client pelo id do interno retornado em cada item (work.internal.id).
  const getWorkByInternalId = async (internalId, internalName) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken || !internalId || !internalName) {
      return;
    }
    try {
      const pageSize = 100;
      const maxPages = 20;
      let offset = 0;
      let currentPage = 0;
      const works = [];

      while (currentPage < maxPages) {
        const response = await api.get('/internals/work/', {
          params: { internal_name: internalName, limit: pageSize, offset },
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        const pageResults = response.data?.results || [];
        works.push(...pageResults.filter(
          (work) => String(work.internal?.id ?? work.internal) === String(internalId)
        ));

        if (!response.data?.next) break;
        offset += pageSize;
        currentPage += 1;
      }

      return { results: works, count: works.length };
    } catch (error) {
      console.error("Erro ao listar trabalhos:", error);
      throw error;
    }
  };

  // Requisição para atualizar trabalho
  const updateWork = async (workId, data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }
    try {
      const response = await api.patch(`/internals/work/${workId}/`, data, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });
      return response.data; // Retorna os dados atualizados
    } catch (error) {
      console.error("Erro ao atualizar trabalho:", error);
      throw error;
    }
  };

  const listWorklaunch = async (workId) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;
    try {
      const response = await api.get('/internals/worklaunch/', {
        params: { internal_work: workId },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao listar lançamentos de trabalho:', error);
      throw error;
    }
  };

  const createWorklaunch = async (data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;
    try {
      const response = await api.post('/internals/worklaunch/', data, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });
      return response;
    } catch (error) {
      console.error('Erro ao criar lançamento de trabalho:', error);
      throw error;
    }
  };

  const updateWorklaunch = async (id, data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;
    try {
      const response = await api.patch(`/internals/worklaunch/${id}/`, data, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar lançamento de trabalho:', error);
      throw error;
    }
  };

  const listTypeUse = async () => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;
    try {
      const response = await api.get('/units/type_use/', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao listar tipos de uso:', error);
      return { results: [] };
    }
  };

  const listTypeSpace = async () => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;
    try {
      const response = await api.get('/units/typelocation/', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao listar tipos de espaço:', error);
      return { results: [] };
    }
  };

  const createLocation = async (data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;
    try {
      const response = await api.post('/units/location/', data, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });
      return response;
    } catch (error) {
      console.error('Erro ao criar local:', error);
      throw error;
    }
  };

  // Adicionar notas e faltas diciplinares
  const createDisciplineNote = async (data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }
    try {
      const response = await api.post('/internals/internalnotes/', data, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });
      toast.success('Nota realizado com sucesso');
      return response;
    } catch (error) {
      console.error("Erro ao criar nota:", error);
      toast.error('Erro ao salvar, tente novamente');
      throw error;
    }
  };

  const listDisciplineNotes = async (params = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }
    try {
      const response = await api.get('/internals/internalnotes/', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params,
      });
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 403) {
        // Silenciar erros de permissão
        return [];
      }
      // Não mostrar para outros erros
      console.error("Erro ao listar notas disciplinares:", error);
      throw error;
    }
  };

  const createDisciplinaryFault = async (data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }
    try {
      const response = await api.post('/internals/notes_disciplinary_data/', data, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });
      toast.success('Nota salva com sucesso');
      return response;
    } catch (error) {
      console.error("Erro ao criar nota:", error);
      toast.error('ero ao salvar, tente novamente');
      throw error;
    }
  };

  const listDisciplinaryFaults = async (params = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }
    try {
      const response = await api.get('/internals/notes_disciplinary_data/', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params,
      });
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 403) {
        // Silenciar erros de permissão
        return [];
      }
      // Não mostrar para outros erros
      console.error("Erro ao listar faltas:", error);
      throw error;
    }
  };

  // Parentes
  const listKin = async (params) => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get("internals/kin/", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params,
      });
      return response.data;
    } catch (error) {
      console.error("Erro ao listar kin:", error);
      return null;
    }
  };

  const createKin = async (data) => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.post("internals/kin/", data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success('Parente salvo com sucesso');

      return response.data;
    } catch (error) {
      console.error("Erro ao criar kin:", error);
      toast.error('Erro ao salvar parente');
      return null;
    }
  };

  const updateKin = async (id, data) => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.patch(`internals/kin/${id}/`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success("Kin atualizado:", response.data);
      return response.data;
    } catch (error) {
      console.error("Erro ao atualizar kin:", error);
      toast.error('Erro ao salvar parente');
      return null;
    }
  };

  const addDeficiencyInternal = async (data) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await api.post('internals/internaldeficiency/', data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Deficiência registrada com sucesso');
      return response.data;
    } catch (error) {
      console.error('Erro ao registrar deficiência', error);
      toast.error('Erro ao registrar deficiência');
      return null;
    }
  };

  const listDeficiencyInternal = async (internalId) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await api.get(`internals/internaldeficiency/internal/${internalId}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar deficiências do interno', error);
      return null;
    }
  };

  const updateDeficiencyInternal = async (id, data) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await api.patch(`internals/internaldeficiency/${id}/`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Deficiência atualizada com sucesso');
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar deficiência', error);
      toast.error('Erro ao atualizar deficiência');
      return null;
    }
  };

  // Soft delete - Imagem do interno
  const updateInternalImageActive = async (imageId, isActive = false) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.patch(`/internals/internalimage/${imageId}/`, { is_active: isActive }, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      toast.success(isActive ? 'Imagem restaurada com sucesso' : 'Imagem removida com sucesso');
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar imagem', error);
      const apiMessage = getApiErrorMessage(error);
      toast.error(apiMessage || 'Erro ao atualizar imagem');
    }
  };

  // Soft delete - Arquivo/Documento do interno
  const updateInternalFileActive = async (fileId, isActive = false) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.patch(`/internals/internalfile/${fileId}/`, { is_active: isActive }, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      toast.success(isActive ? 'Documento restaurado com sucesso' : 'Documento removido com sucesso');
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar documento', error);
      const apiMessage = getApiErrorMessage(error);
      toast.error(apiMessage || 'Erro ao atualizar documento');
    }
  };

  // Soft delete - Arquivo do visitante
  const updateVisitorFileActive = async (fileId, isActive = false) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.patch(`/internals/visitorfile/${fileId}/`, { is_active: isActive }, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      toast.success(isActive ? 'Documento restaurado com sucesso' : 'Documento removido com sucesso');
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar documento do visitante', error);
      const apiMessage = getApiErrorMessage(error);
      toast.error(apiMessage || 'Erro ao atualizar documento do visitante');
    }
  };

  const createVisitorImage = async (formData) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      const response = await api.post('/internals/visitorimage/', formData, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success('Foto adicionada com sucesso!');
      return response.data;
    } catch (error) {
      console.error('Erro ao adicionar foto do visitante:', error);
      const apiMessage = getApiErrorMessage(error);
      toast.error(apiMessage || 'Erro ao adicionar foto do visitante');
    }
  };

  const updateVisitorImage = async (imageId, formData) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    try {
      const response = await api.patch(`/internals/visitorimage/${imageId}/`, formData, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success('Foto atualizada com sucesso!');
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar foto do visitante:', error);
      const apiMessage = getApiErrorMessage(error);
      toast.error(apiMessage || 'Erro ao atualizar foto do visitante');
    }
  };

  // Arquivos (imagens/documentos) não vêm mais como URL no JSON: o conteúdo sai pela rota
  // binary do recurso, autenticada. `resource` é o caminho do recurso (BINARY_RESOURCES em
  // utils/binaryFile.js), `id` é o do registro dono do arquivo e `field` o campo do arquivo.
  const getBinaryFile = useCallback(async (resource, id, field, options = {}) => {
    const response = await api.get(`/${resource}/binary/${id}/`, {
      params: field ? { field } : undefined,
      responseType: 'blob',
      signal: options.signal,
    });
    return response.data;
  }, []);

  const getBinaryFileUrl = useCallback((resource, id, field) => {
    const query = field ? `?field=${encodeURIComponent(field)}` : '';
    return `${api.defaults.baseURL}/${resource}/binary/${id}/${query}`;
  }, []);

  // Abre o arquivo em nova aba. A aba é aberta ainda no clique (senão o navegador bloqueia
  // como pop-up) e só recebe o arquivo depois do download autenticado.
  const openBinaryFile = useCallback(async ({ resource, id, field }) => {
    if (!resource || !id) return;

    const newWindow = window.open('', '_blank');
    try {
      const blob = await getBinaryFile(resource, id, field);
      const objectUrl = URL.createObjectURL(blob);
      if (newWindow) {
        newWindow.location.href = objectUrl;
      } else {
        window.open(objectUrl, '_blank');
      }
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    } catch (error) {
      if (newWindow) newWindow.close();
      console.error('Erro ao abrir arquivo', error);
      toast.error('Não foi possível abrir o arquivo.');
    }
  }, [getBinaryFile]);

  // Soft delete - Imagem do visitante
  const updateVisitorImageActive = async (imageId, isActive = false) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.patch(`/internals/visitorimage/${imageId}/`, { is_active: isActive }, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      toast.success(isActive ? 'Imagem restaurada com sucesso' : 'Imagem removida com sucesso');
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar imagem do visitante', error);
      const apiMessage = getApiErrorMessage(error);
      toast.error(apiMessage || 'Erro ao atualizar imagem do visitante');
    }
  };

  return (
    <InternalsContext.Provider
      value={{
        getBinaryFile,
        getBinaryFileUrl,
        openBinaryFile,
        listInternalHome,
        listQuantitative,
        listInternals,
        createInternal,
        listInternalInclusion,
        createInternalInclusion,
        updateInternalInclusion,
        getInternalById,
        listInternalsName,
        updateInternal,
        entryInternal,
        searchInternal,
        listInternalConduct,
        listInternalRegime,
        createFactionrelationship,
        updateFactionrelationship,
        listFactionrelationship,
        listFactions,
        listFunctionFaction,
        uploadInternalImage,
        listInternalImage,
        updateInternalImageMain,
        updateInternalImageActive,
        listTypeImage,
        listAddress,
        listAddressByInternal,
        createAddress,
        updateAddress,
        listFiles,
        addFiles,
        updateInternalFileActive,
        createPhone,
        listPhone,
        updatePhone,
        addProcess,
        listProcessJudicialDistricts,
        listProcessCriminalClassification,
        getProcessInternalById,
        getProcessInternals,
        getProcessById,
        updateProcess,
        getCicles,
        updateCicle,
        listDetailType,
        listInternalsByCell,
        changeCell,
        createTransfer,
        listTransfers,
        acceptTransfer,
        rejectTransfer,
        cancelTransfer,
        updateTransfer,
        listTypeLeaving,
        createLeavingRecord,
        listTypeEntry,
        createEntryRecord,
        listTemporaryLeavingRecords,
        getLastPendingTemporaryLeavingRecord,
        internalTemporaryReturn,
        internalRecapture,
        internalHospitalReturn,
        addSocialService,
        listSocialService,
        addAssistance,
        listAssistance,
        updateSocialService,
        addPsychology,
        listPsychology,
        addPsychologicalCare,
        listPsychologicalCare,
        updatePsychology,
        createHealth,
        listHealthInternal,
        updateHealth,
        listAdmissions,
        createAdmission,
        updateAdmission,
        listTuberculosis,
        createTuberculosis,
        updateTuberculosis,
        listTuberculosisQuestions,
        listTuberculosisCatalogs,
        listHanseniase,
        createHanseniase,
        updateHanseniase,
        listSpontaneousDemand,
        createSpontaneousDemand,
        createInternalHealthCare,
        listInternalHealthCare,
        updateInternalHealthCare,
        listForwardings,
        createForwarding,
        updateForwarding,
        listPrescriptions,
        createPrescription,
        updatePrescription,
        addHealthEvolution,
        listHealthEvolution,
        updateHealthEvolution,
        listExaminations,
        createExamination,
        updateExamination,
        listMedicalTest,
        createMedicalTest,
        listVitalSigns,
        createVitalSigns,
        updateVitalSigns,
        createNutrition,
        listNutrition,
        updateNutrition,
        addNutritionEvolution,
        listNutritionEvolution,
        createOdontology,
        listOdontology,
        updateOdontology,
        addOdontologyEvolution,
        listOdontologyEvolution,
        addEducation,
        listEducation,
        updateEducation,
        addEducationEvolution,
        listEducationEvolution,
        createSchedule,
        listSchedule,
        updateSchedule,
        createVisitor,
        getVisitors,
        getVisitorsByInternalId,
        getVisitorById,
        listVisitorFiles,
        listVisitorAddresses,
        createVisitorAddress,
        updateVisitorAddress,
        listVisitorPhone,
        createVisitorPhone,
        updateVisitorPhone,
        listVisitorImages,
        createVisitorImage,
        updateVisitorImage,
        createVisitorFile,
        updateVisitorFileActive,
        updateVisitorImageActive,
        updateVisitor,
        listVisitorRelationships,
        createVisitorRelationship,
        updateVisitorRelationship,
        deleteVisitorRelationship,
        respondVisitorAuthorization,
        createVisitorOthers,
        consultLawyerOab,
        listOtherPeople,
        createOtherPeople,
        createMoviment,
        updateMovimentRegister,
        getMoviments,
        createPackageDelivery,
        listPackageDeliveries,
        createWork,
        getWorkByInternalId,
        updateWork,
        listWorklaunch,
        createWorklaunch,
        updateWorklaunch,
        listTypeUse,
        listTypeSpace,
        createLocation,
        createDisciplineNote,
        createDisciplinaryFault,
        listDisciplinaryFaults,
        listDisciplineNotes,
        createVaccination,
        listVaccinationsByInternal,
        createDosage,
        listDosageInternal,
        updateDosage,
        createExternalHealth,
        listExternalHealthByInternal,
        listKin,
        createKin,
        updateKin,
        addDeficiencyInternal,
        listDeficiencyInternal,
        updateDeficiencyInternal,

      }}>

      {children}
    </InternalsContext.Provider>
  );
};
